import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useProfile';
import { useUnreadComments } from '../../hooks/useUnreadComments';
import { supabase } from '../../lib/supabase';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user } = useAuth();
  const { profile } = useProfile(user?.id || null);
  const { unreadCount } = useUnreadComments(user?.id || null);
  const navigate = useNavigate();

  const isWriter = profile?.role === 'writer';

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-900/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://static.readdy.ai/image/cd80e8551adaa83aa4e45d1e4f56c590/c334bfc7bb42624cba9a352752f4949a.png"
              alt="Sinister Files Logo"
              className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-red-600 font-['Cinzel'] tracking-wider">
                SINISTER FILES
              </span>
              <span className="text-xs text-gray-400 font-['Crimson_Text'] italic">
                Unearthing Dark Stories
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              Home
            </Link>
            <Link to="/stories" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              Stories
            </Link>
            <Link to="/top-ranked" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              Top Ranked
            </Link>
            <Link to="/writers" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              Writers
            </Link>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              About
            </Link>
            <Link to="/community" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
              Community
            </Link>
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {isWriter && (
                  <Link
                    to="/submit"
                    className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-quill-pen-line"></i>
                    Write Story
                  </Link>
                )}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 cursor-pointer relative"
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-red-900 to-red-700">
                      {profile?.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.display_name || profile.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-white text-sm"></i>
                      )}
                    </div>
                    {isWriter && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-black">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsUserMenuOpen(false)}
                      ></div>
                      <div className="absolute right-0 top-full mt-2 w-48 bg-[#1a1a1a] border border-red-900/20 rounded-lg shadow-xl z-50">
                        <div className="p-2">
                          <Link
                            to={isWriter ? '/profile/writer' : '/profile/reader'}
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-red-900/10 rounded cursor-pointer whitespace-nowrap relative"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-user-line mr-2"></i>
                            Profile
                            {isWriter && unreadCount > 0 && (
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                              </span>
                            )}
                          </Link>
                          <hr className="my-2 border-red-900/20" />
                          <button
                            onClick={handleSignOut}
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/10 rounded cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-logout-box-line mr-2"></i>
                            Sign Out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white cursor-pointer"
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-red-900/20">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/stories"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Stories
              </Link>
              <Link
                to="/top-ranked"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Top Ranked
              </Link>
              <Link
                to="/writers"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Writers
              </Link>
              <Link
                to="/about"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/community"
                className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Community
              </Link>
              
              {user ? (
                <>
                  <hr className="border-red-900/20" />
                  {isWriter && (
                    <Link
                      to="/submit"
                      className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-sm cursor-pointer whitespace-nowrap"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-quill-pen-line mr-1"></i>
                      Write Story
                    </Link>
                  )}
                  <Link
                    to={isWriter ? '/profile/writer' : '/profile/reader'}
                    className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap relative inline-flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="ri-user-line mr-2"></i>
                    Profile
                    {isWriter && unreadCount > 0 && (
                      <span className="ml-2 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsMenuOpen(false);
                    }}
                    className="text-left text-red-400 hover:text-red-300 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-logout-box-line mr-2"></i>
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <hr className="border-red-900/20" />
                  <Link
                    to="/login"
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium text-center cursor-pointer whitespace-nowrap"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}